<template>
  <section class="max-w-[1440px] mx-auto px-12 py-12 space-y-12">

    <!-- HEADER -->
    <header class="text-center max-w-3xl mx-auto">
      <h2 class="text-4xl font-black tracking-tight">
        Protect & Own Your Audience
      </h2>
      <p class="text-slate-400 mt-3">
        Choose a membership plan to insure your verified fan relationships,
        unlock audience intelligence, and enable brand visibility.
      </p>
    </header>

    <!-- PLANS -->
    <section class="grid grid-cols-1 md:grid-cols-3 gap-10">

      <div
        v-for="plan in plans"
        :key="plan.id"
        class="bg-white p-10 rounded-[2.5rem] premium-shadow
               border border-slate-50 flex flex-col justify-between
               relative"
        :class="plan.popular ? 'ring-2 ring-slate-900' : ''"
      >
        <!-- Badge -->
        <span
          v-if="plan.popular"
          class="absolute -top-4 left-1/2 -translate-x-1/2
                 bg-slate-900 text-white text-xs font-bold
                 px-4 py-1 rounded-full uppercase tracking-wider"
        >
          Most Popular
        </span>

        <!-- Header -->
        <div>
          <h3 class="text-2xl font-black">
            {{ plan.name }}
          </h3>
          <p class="text-slate-400 mt-1 text-sm">
            {{ plan.tagline }}
          </p>

          <div class="flex gap-2 flex-wrap mb-2">

            <!-- Discount -->
            <span
              v-if="plan.discount > 0"
              class="badge-discount"
            >
              {{ plan.discount }}% OFF
            </span>

            <!-- Priority -->
            <span
              v-if="plan.features.includes('Priority Support')"
              class="badge-priority"
            >
              Priority
            </span>

          </div>
          <div class="mt-6">
           <div class="flex items-end gap-2">

              <span class="text-5xl font-black text-primary">
                ₹ {{ plan.finalPrice }}
              </span>

              <span
                v-if="plan.discount > 0"
                class="text-lg line-through text-slate-400"
              >
                ₹ {{ plan.price }}
              </span>

            </div>
            <span class="text-slate-400 text-sm">/ month</span>
          </div>
        </div>

        <!-- Features -->
        <ul class="mt-8 space-y-3 text-sm font-semibold">
          <li v-for="f in plan.features" :key="f">✔ {{ f }}</li>
        </ul>

        <!-- CTA -->
        <button
          @click="selectPlan(plan)"
          :disabled="currentPlan === plan.id"
          class="mt-10 py-4 rounded-xl font-bold text-xs
                 uppercase tracking-wider transition"
          :class="currentPlan === plan.id
            ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
            : 'bg-primary text-white hover:opacity-90'"
        >
          {{ currentPlan === plan.id ? "Active Plan" : "Choose Plan" }}
        </button>
      </div>

    </section>

    <!-- PAYMENT MODAL (MVP SIMULATION) -->
   <div v-if="showPayment" class="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">

  <div class="bg-white rounded-[2rem] p-10 w-full max-w-md relative">

    <!-- Close -->
    <button
      @click="showPayment = false"
      class="absolute top-4 right-4 text-slate-400 hover:text-black"
    >
      ✕
    </button>

    <!-- Title -->
    <h3 class="text-2xl font-black">
      Confirm Subscription
    </h3>

    <p class="text-slate-400 mt-2">
      You're subscribing to <strong>{{ selectedPlan.name }}</strong>
    </p>

    <!-- PRICE BOX -->
    <div class="mt-6 bg-slate-50 p-5 rounded-xl space-y-3">

      <!-- Original -->
      <div class="flex justify-between text-sm">
        <span>Plan Price</span>
        <span>₹{{ selectedPlan.price }}</span>
      </div>

      <!-- Discount -->
      <div
        v-if="selectedPlan.discount > 0"
        class="flex justify-between text-sm text-red-500 font-semibold"
      >
        <span>Discount ({{ selectedPlan.discount }}%)</span>
        <span>-₹{{ Math.round(selectedPlan.price * selectedPlan.discount / 100) }}</span>
      </div>

      <!-- Divider -->
      <div class="border-t my-2"></div>

      <!-- Final -->
      <div class="flex justify-between font-bold text-lg">
        <span>Total Payable</span>
        <span>₹{{ selectedPlan.finalPrice }}</span>
      </div>

    </div>

    <!-- CTA -->
    <div class="mt-8 flex gap-4">

      <button
        @click="confirmPayment"
        class="bg-primary text-white flex-1 py-4 rounded-xl
               font-bold text-xs uppercase tracking-wider
               hover:opacity-90 transition"
      >
        Pay ₹{{ selectedPlan.finalPrice }}
      </button>

      <button
        @click="showPayment = false"
        class="flex-1 py-4 rounded-xl border
               text-xs font-bold uppercase tracking-wider"
      >
        Cancel
      </button>

    </div>

    <!-- Footer note -->
    <p class="text-xs text-slate-400 mt-4 text-center">
      Secure payment via Razorpay / Stripe
    </p>

  </div>
</div>
  </section>
</template>

<script>
import { loadPlansApi } from "@/apis/planapis";
import { apiBaseUrl } from "@/apis/appconst";
import { chkPlanApi,upgradePlanApi } from "@/apis/authapis";

import { useToast } from "vue-toastification";
import "vue-toastification/dist/index.css";

export default {
  name: "OurPlans",

  data() {
    return {
      user_id: "",
      toast: null,

      plans: [],
      currentPlan: null,

      showPayment: false,
      selectedPlan: {}
    };
  },

  async mounted() {

    this.toast = useToast();

    const params = new URLSearchParams(window.location.search);
    const msg = params.get("msg");

    if (msg) {
        // console.log(msg);
        this.toast.success(decodeURIComponent(msg));
    }

    try {

      const ldataRaw = sessionStorage.getItem("ldata");

      if (ldataRaw && ldataRaw !== "undefined") {

        const ldata = JSON.parse(ldataRaw);

        if (ldata && ldata.user_id) {
          this.user_id = ldata.user_id;
          this.currentPlan = ldata.user_plan || null;
        }
      }

    } catch (e) {

      console.log("Session parse error", e);
    }

    await this.loadPlans();
    this.chkPlanNow();
  },

  methods: {

    async loadPlans() {

      const res = await loadPlansApi();

      if (res && res.status && res.records) {

        // this.plans = res.records.map(p => ({

        //   id: p.mplan_id,
        //   name: p.mplan_name,
        //   tagline: p.mplan_code || "",
        //   price: p.mplan_price,

        //   features: p.mplan_features
        //     ? JSON.parse(p.mplan_features)
        //     : [],

        //   popular: p.mplan_discovery_boost
        // }));

       this.plans = res.records.map(p => {

  const price = Number(p.mplan_price || 0);
  const discount = Number(p.mplan_discovery_boost || 0);

  const finalPrice = discount > 0
    ? Math.round(price - (price * discount / 100))
    : price;

  let features = p.mplan_features
    ? JSON.parse(p.mplan_features)
    : [];

  if (p.mplan_priority_support === "yes") {
    features.push("Priority Support");
  }

  return {
    id: p.mplan_id,
    name: p.mplan_name,
    tagline: p.mplan_code || "",

    price: price,
    finalPrice: finalPrice,
    discount: discount,

    features: features,

    popular: discount >= 10
  };

}).sort((a, b) => a.finalPrice - b.finalPrice); // ✅ ascending order

      } else {

        this.toast.error("Failed to load plans");
        this.plans = [];
      }
    },

    async selectPlan(plan) {
      if(plan.price.length<=0){
         const res = await upgradePlanApi(this.user_id,plan.id);

          if (res && res.status) {
            this.toast.success("Plan Upgraded");
             await this.loadPlans();
              this.chkPlanNow();
          }
          else{
            this.toast.error("Plan Upgrade Failed");
          }
        return;
      }
      else{
        if (this.currentPlan === plan.id) return;

        this.selectedPlan = plan;
        this.showPayment = true;
      }
    },
    async chkPlanNow(){
      
      const res = await chkPlanApi(this.user_id);

      if (res && res.status) {
        var user_dt = res.user_dt;
        this.currentPlan = user_dt.user_membership_plan;
        // alert(this.currentPlan+"");
        this.showPayment = false;

        try {
          const ldataRaw = sessionStorage.getItem("ldata");
          const ldata = JSON.parse(ldataRaw);

          ldata.user_plan = user_dt.user_membership_plan;

          sessionStorage.setItem("ldata", JSON.stringify(ldata));
        } catch {
          
        console.log("Session parse error");
        }

      }
    },
   async confirmPayment() {

      if (!this.user_id || !this.selectedPlan?.id) {
        this.toast.error("Invalid payment request");
        return;
      }

      try {

        const amount = this.selectedPlan.finalPrice || this.selectedPlan.price;

        const url = `${apiBaseUrl}razorpay/redirectToPaymentGateway?plan_id=${this.selectedPlan.id}&uid=${this.user_id}&amount=${amount}`;

        // optional loader UX
        this.toast.success("Redirecting to payment...");

        window.location.href = url;

      } catch (err) {
        console.error(err);
        this.toast.error("Payment initialization failed");
      }
    }
  }
};
</script>
<style scoped>
.badge-discount {
  background: #fee2e2;
  color: #dc2626;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
}

.badge-priority {
  background: #dcfce7;
  color: #166534;
  font-size: 11px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
}
</style>